import { differenceInDays, parseISO } from 'date-fns';

export interface TBRBook {
  id: string;
  book_id: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  shelf: string;
  tbr_category?: string;
  books?: {
    id: string;
    title: string;
    authors: string[];
    cover_url?: string;
    cover_cache_url?: string;
  };
}

export interface TBRAnalyticsStats {
  totalTBRBooks: number;
  totalReadBooks: number;
  conversionRate: number;
  averageTBRDays: number;
  categoryPerformance: Array<{
    category: string;
    count: number;
    readCount: number;
    conversionRate: number;
    averageDays: number;
  }>;
  stuckBooks: Array<{
    id: string;
    bookId: string;
    title: string;
    authors: string[];
    coverUrl?: string;
    daysOnTBR: number;
    category?: string;
  }>;
  fastestReads: Array<{
    title: string;
    authors: string[];
    daysOnTBR: number;
    category?: string;
  }>;
}

/**
 * Calculate comprehensive TBR analytics
 */
export function calculateTBRAnalytics(
  currentTBR: TBRBook[],
  historicalData: TBRBook[]
): TBRAnalyticsStats {
  // Books that were read (moved from TBR to read)
  const readBooks = historicalData.filter(
    book => book.shelf === 'read' && book.started_at
  );

  // Calculate days on TBR for read books
  const booksWithTBRTime = readBooks
    .map(book => {
      const addedDate = parseISO(book.created_at);
      const startedDate = book.started_at ? parseISO(book.started_at) : null;
      
      if (!startedDate) return null;
      
      const days = differenceInDays(startedDate, addedDate);
      return {
        book,
        days: days > 0 ? days : 0,
      };
    })
    .filter((item): item is { book: TBRBook; days: number } => item !== null);

  const totalTBRBooks = currentTBR.length;
  const totalReadBooks = readBooks.length;
  const conversionRate = totalTBRBooks + totalReadBooks > 0
    ? Math.round((totalReadBooks / (totalTBRBooks + totalReadBooks)) * 100)
    : 0;

  const averageTBRDays = booksWithTBRTime.length > 0
    ? Math.round(
        booksWithTBRTime.reduce((sum, item) => sum + item.days, 0) / booksWithTBRTime.length
      )
    : 0;

  // Category performance analysis
  const categoryMap = new Map<string, {
    total: number;
    read: number;
    totalDays: number;
    daysCount: number;
  }>();

  // Count current TBR by category
  currentTBR.forEach(book => {
    const category = book.tbr_category || 'Uncategorized';
    const current = categoryMap.get(category) || { total: 0, read: 0, totalDays: 0, daysCount: 0 };
    current.total++;
    categoryMap.set(category, current);
  });

  // Add read books by category
  booksWithTBRTime.forEach(({ book, days }) => {
    const category = book.tbr_category || 'Uncategorized';
    const current = categoryMap.get(category) || { total: 0, read: 0, totalDays: 0, daysCount: 0 };
    current.read++;
    current.total++;
    current.totalDays += days;
    current.daysCount++;
    categoryMap.set(category, current);
  });

  const categoryPerformance = Array.from(categoryMap.entries())
    .map(([category, data]) => ({
      category,
      count: data.total,
      readCount: data.read,
      conversionRate: data.total > 0 ? Math.round((data.read / data.total) * 100) : 0,
      averageDays: data.daysCount > 0 ? Math.round(data.totalDays / data.daysCount) : 0,
    }))
    .sort((a, b) => b.readCount - a.readCount);

  // Identify books stuck on TBR (6+ months)
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  const stuckBooks = currentTBR
    .map(book => {
      const addedDate = parseISO(book.created_at);
      const daysOnTBR = differenceInDays(new Date(), addedDate);
      
      return {
        id: book.id,
        bookId: book.book_id,
        title: book.books?.title || 'Unknown',
        authors: book.books?.authors || [],
        coverUrl: book.books?.cover_cache_url || book.books?.cover_url,
        daysOnTBR,
        category: book.tbr_category,
        addedDate,
      };
    })
    .filter(book => book.addedDate < sixMonthsAgo)
    .sort((a, b) => b.daysOnTBR - a.daysOnTBR)
    .map(({ addedDate, ...rest }) => rest);

  // Find fastest reads (books that went from TBR to read quickly)
  const fastestReads = booksWithTBRTime
    .filter(item => item.days <= 30) // Read within 30 days
    .sort((a, b) => a.days - b.days)
    .slice(0, 5)
    .map(({ book, days }) => ({
      title: book.books?.title || 'Unknown',
      authors: book.books?.authors || [],
      daysOnTBR: days,
      category: book.tbr_category,
    }));

  return {
    totalTBRBooks,
    totalReadBooks,
    conversionRate,
    averageTBRDays,
    categoryPerformance,
    stuckBooks,
    fastestReads,
  };
}
